import { useState } from "react";
import api, { useList, errMsg } from "@/lib/api";
import { PageHeader, StatusBadge, Spinner, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Send } from "lucide-react";

export default function TeachingRequestPage() {
  const { data: courses, loading: lc } = useList("/courses");
  const { data: requests, loading: lr, reload } = useList("/teacher/teaching-requests");
  const [courseId, setCourseId] = useState("");
  const [levelIds, setLevelIds] = useState(new Set());
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  if (lc || lr) return <Spinner full />;
  const activeCourses = (courses || []).filter((c) => c.status !== "inactive");
  const selectedCourse = activeCourses.find((c) => c.id === courseId);

  const submit = async () => {
    if (!courseId) { toast.error("Pick a course first"); return; }
    setBusy(true);
    try {
      await api.post("/teacher/teaching-requests", {
        course_id: courseId,
        level_ids: [...levelIds],
        note: note || null,
      });
      toast.success("Request submitted — admin has been notified");
      setCourseId(""); setLevelIds(new Set()); setNote("");
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="teaching-request-page">
      <PageHeader title="Request to teach a course"
        description="Pick a course (and optionally specific grades) you'd like to be approved for. Admin will review your request." />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-lg border bg-card p-5 space-y-4">
          <h3 className="font-semibold text-base flex items-center gap-2">
            <Plus className="h-4 w-4 text-secondary" />New request
          </h3>
          <div className="space-y-1.5">
            <Label className="text-xs">Course</Label>
            <select
              data-testid="request-course-select"
              className="w-full rounded-md border px-3 py-2 bg-background text-sm"
              value={courseId} onChange={(e) => { setCourseId(e.target.value); setLevelIds(new Set()); }}>
              <option value="">— Pick a course —</option>
              {activeCourses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {selectedCourse && (selectedCourse.levels || []).length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs">Grades / levels (optional)</Label>
              <div className="flex flex-wrap gap-x-3 gap-y-1.5 max-h-40 overflow-y-auto rounded-md border p-2">
                {selectedCourse.levels.map((lv) => (
                  <label key={lv.id} className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <Checkbox checked={levelIds.has(lv.id)}
                      onCheckedChange={() => {
                        const n = new Set(levelIds);
                        n.has(lv.id) ? n.delete(lv.id) : n.add(lv.id);
                        setLevelIds(n);
                      }}
                      data-testid={`request-level-${lv.id}`} />
                    <span>{lv.level_name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">Note for admin (optional)</Label>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. I hold ABRSM Grade 8 piano, happy to take beginners too."
              data-testid="request-note" />
          </div>
          <Button onClick={submit} disabled={busy || !courseId} data-testid="submit-teaching-request">
            <Send className="h-4 w-4 mr-1" />{busy ? "Submitting…" : "Submit request"}
          </Button>
        </div>
        <div className="rounded-lg border bg-card p-5 space-y-3">
          <h3 className="font-semibold text-base">My past requests</h3>
          {(requests || []).length === 0
            ? <EmptyState text="No requests yet." />
            : <div className="space-y-2" data-testid="teaching-request-list">
                {requests.map((r) => (
                  <div key={r.id} className="rounded-md border p-3 flex items-start justify-between gap-3"
                       data-testid={`teaching-request-row-${r.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{r.course_name}</p>
                      {r.note && <p className="text-xs text-muted-foreground mt-0.5">Note: {r.note}</p>}
                      {r.admin_note && <p className="text-xs text-amber-700 mt-0.5">Admin: {r.admin_note}</p>}
                      <p className="text-[10px] text-muted-foreground mt-1">{r.created_at?.slice(0, 10)}</p>
                    </div>
                    <StatusBadge status={r.status} />
                  </div>
                ))}
              </div>}
        </div>
      </div>
    </div>
  );
}
